import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { ProductPropertyDataProp } from "@/store/slices/eDocumentSlides";
import { CANT_EPSILON, roundCant } from "@utils/Cant";
import { EDOCUMENT_TEXT_MAX_LENGTH, sanitizeSafeText } from "@amplifyShared/safeText";

export type EditableProductField =
  | "description"
  | "cant"
  | "price"
  | "porcentaje_descuento";

const EDITABLE_FIELDS: EditableProductField[] = [
  "description",
  "cant",
  "price",
  "porcentaje_descuento",
];

export type ProductFieldCommitResult =
  | { ok: true; update: ProductPropertyDataProp }
  | { ok: false; error?: string };

const localKey = (field: EditableProductField, productId: string) =>
  `${field}-${productId}`;

export const parseProductFieldValue = (
  field: EditableProductField,
  rawValue: string,
  maxValue?: number | false,
): ProductFieldCommitResult => {
  const isMasculine = field === "price" || field === "porcentaje_descuento";
  const propLabel =
    field === "porcentaje_descuento" ? "porcentaje de descuento" : field;

  // La descripcion se normaliza antes de validar: es texto libre que acaba en el nodo
  // <Detalle> del XML y en el payload que viaja al Step Function, donde un backtick, una
  // barra invertida o un surrogate huerfano rompen la serializacion. Al limpiarla aqui la
  // linea nunca entra sucia a Redux ni a DynamoDB, y el efecto que sincroniza
  // `localInputValues` con `documentProducts` deja el input mostrando el valor ya limpio.
  const value =
    field === "description"
      ? sanitizeSafeText(rawValue, EDOCUMENT_TEXT_MAX_LENGTH)
      : `${rawValue}`;

  if (!value) {
    return {
      ok: false,
      error: `${isMasculine ? "El" : "La"} ${propLabel} es obligatoria`,
    };
  }

  if (field === "description") {
    return {
      ok: true,
      update: { id: "", property: field, value },
    };
  }

  // Acepta coma decimal (teclados es-CR): "1,5" -> "1.5"
  let numericValue = Number(value.replace(",", "."));
  if (isNaN(numericValue) || numericValue < 0) {
    return {
      ok: false,
      error: `El valor de ${isMasculine ? "El" : "La"} ${propLabel} no es valido`,
    };
  }

  if (field === "cant") {
    numericValue = roundCant(numericValue);
  }

  if (maxValue && !isNaN(maxValue) && maxValue + CANT_EPSILON < numericValue) {
    return {
      ok: false,
      error: `El valor de ${isMasculine ? "El" : "La"} ${propLabel} excelede el limite permitido`,
    };
  }

  return {
    ok: true,
    update: { id: "", property: field, value: numericValue },
  };
};

const reduxValueForField = (
  product: EDocumentProductDataType,
  field: EditableProductField,
): string => {
  switch (field) {
    case "description":
      return product.description ?? "";
    case "cant":
      return product.cant?.toString() ?? "";
    case "price":
      return product.price?.toString() ?? "";
    case "porcentaje_descuento":
      return product.porcentaje_descuento?.toString() ?? "0";
  }
};

const valuesEqual = (
  field: EditableProductField,
  localValue: string,
  reduxValue: string,
): boolean => {
  if (field === "description") {
    // El valor local es lo que tecleo el usuario y el de Redux ya esta normalizado, asi
    // que la comparacion tiene que normalizar los dos lados.
    return (
      sanitizeSafeText(localValue, EDOCUMENT_TEXT_MAX_LENGTH) ===
      sanitizeSafeText(reduxValue, EDOCUMENT_TEXT_MAX_LENGTH)
    );
  }
  const localNum = Number(`${localValue}`.replace(",", "."));
  const reduxNum = Number(reduxValue);
  if (!isNaN(localNum) && !isNaN(reduxNum)) {
    if (field === "cant") {
      return roundCant(localNum) === roundCant(reduxNum);
    }
    return localNum === reduxNum;
  }
  return localValue === reduxValue;
};

export type GetPendingUpdatesOptions = {
  documentNoteTable?: boolean;
  priceNote?: boolean;
  maxCantByProductId?: Record<string, number | false>;
};

export const getPendingProductUpdates = (
  localInputValues: Record<string, string>,
  documentProducts: EDocumentProductDataType[],
  options: GetPendingUpdatesOptions = {},
): ProductPropertyDataProp[] => {
  const { documentNoteTable = false, maxCantByProductId = {} } = options;
  const updates: ProductPropertyDataProp[] = [];

  documentProducts
    .filter((p) => !p.toDelete)
    .forEach((product) => {
      const productId = `${product.id}`;
      const fields: EditableProductField[] = documentNoteTable
        ? ["cant"]
        : EDITABLE_FIELDS;

      fields.forEach((field) => {
        const key = localKey(field, productId);
        const localValue = localInputValues[key];
        if (localValue === undefined) {
          return;
        }

        const reduxValue = reduxValueForField(product, field);
        if (valuesEqual(field, localValue, reduxValue)) {
          return;
        }

        const maxValue =
          field === "cant" ? maxCantByProductId[productId] : undefined;
        const parsed = parseProductFieldValue(field, localValue, maxValue);
        if (!parsed.ok) {
          return;
        }

        updates.push({
          ...parsed.update,
          id: productId,
        });
      });
    });

  return updates;
};

export const getPendingDocumentDiscount = (
  localDiscount: number,
  documentDiscount: number,
): number | null => {
  if (localDiscount === documentDiscount) {
    return null;
  }
  if (isNaN(localDiscount) || localDiscount < 0 || localDiscount > 100) {
    return null;
  }
  return localDiscount;
};
